/**
 * App bar — the top strip of the window.
 *
 * Branding on the left, the open workspace in the middle, actions on the right.
 * On a host that hides the native title bar it is also the window's drag handle,
 * so it renders on the welcome layout too: without it a workspace-less window
 * could not be moved.
 *
 * Two contracts keep this free of platform knowledge:
 *
 *   composition — the active ApplicationProfile's `shell.appBar` spec says what
 *     is on the bar; clicks are dispatched as command ids, exactly like the
 *     status bar. Extensions add their own actions through `addItem`, which is
 *     the same registry the profile items go through.
 *   geometry — the HOST reports where the native window controls sit by writing
 *     `--window-inset-left` / `--window-inset-right` (and
 *     `data-window-chrome` / `data-window-fullscreen`) on the document element.
 *     The bar only pads by those values, so macOS traffic lights, the Windows
 *     control overlay and a browser tab all work without a branch here.
 *
 * The layout CSS lives in the hosts and knows nothing about a top row, so the
 * two `#punica-editor` rules below are injected. They touch height and
 * `grid-template-rows` ONLY: `grid-template-columns` / `grid-template-areas`
 * belong to the docked-sidebar controller (`../sidebarDock/index.js`), and both
 * sets of rules have to coexist.
 */

import type { shell } from 'punica';
import { Helpers } from '../view/helpers.js';

import appBarCss from './appBar.css';

type AppBarSpec = shell.AppBar.AppBarSpec;
type AppBarItem = shell.AppBar.AppBarItem;
type AppBarMenuItem = shell.AppBar.AppBarMenuItem;
type IAppBarManager = shell.AppBar.IAppBarManager;

const BAR_ID = 'punica-appbar';
const STYLE_ID = 'shell-classic-appbar-style';
const LAYOUT_ROOT_ID = 'punica-editor';
/** Set on `<html>`, so a layout re-render can never drop it. */
const ROOT_FLAG = 'punicaAppbar';
const HEIGHT_VAR = '--punica-appbar-height';
/** Status bar row height, as fixed by both hosts' `#statusbar` rule. */
const STATUSBAR_HEIGHT = 24;
/**
 * Activity bar column width, as fixed by both hosts' `#punica-editor`
 * `grid-template-columns` (and mirrored by `../sidebarDock/index.js`). The brand
 * mark centres in a box this wide so it sits on the same vertical axis as the
 * view icons directly below it.
 */
const ACTIVITY_BAR_WIDTH = 48;
/**
 * Matches the height macOS gives its own title bar, so the app's strip never
 * stands taller than the OS chrome directly above it. The Electron host's
 * `trafficLightPosition.y` is derived from this: `(30 - 14) / 2`.
 */
const DEFAULT_HEIGHT = 30;

/**
 * The ids and measurements `./appBar.css` writes out as literals. A `.css` file
 * cannot read a TS constant, so renaming one here would leave the stylesheet
 * pointing at the old name with nothing to complain — the drift guard in
 * `__tests__/appBar.test.ts` checks the injected sheet against these.
 */
export const CSS_TOKENS = Object.freeze({
  BAR_ID,
  LAYOUT_ROOT_ID,
  HEIGHT_VAR,
  STATUSBAR_HEIGHT,
  ACTIVITY_BAR_WIDTH,
  DEFAULT_HEIGHT
});

/** The command ids currently registered, for feature detection. */
function registeredCommands(): Set<string> {
  try {
    const all = punica.kernel.Commands.manager.getAllCommands?.() ?? [];
    return new Set(all.map((entry) => entry.id));
  } catch {
    // No snapshot surface → assume every declared command exists rather than
    // hiding actions that would have worked.
    return new Set();
  }
}

function hasCommand(known: Set<string>, id?: string): boolean {
  if (!id) return false;
  return known.size === 0 ? true : known.has(id);
}

function execute(commandId: string): void {
  try {
    punica.kernel.Commands.manager.executeCommand(commandId);
  } catch (error) {
    console.error('[AppBar] Failed to execute command', commandId, error);
  }
}

/** Workspace folder name from the runtime, or null when none is open. */
async function workspaceName(): Promise<string | null> {
  try {
    const ws = await punica.runtime?.workspace?.getCurrent?.();
    const uri = ws?.rootUri;
    if (!uri) return null;
    const path = decodeURIComponent(String(uri)).replace(/[\\/]+$/, '');
    const name = path.split(/[\\/]/).pop();
    return name || null;
  } catch {
    return null;
  }
}

class AppBarManager implements IAppBarManager {
  #bar: HTMLElement | null = null;
  #left: HTMLElement | null = null;
  #center: HTMLElement | null = null;
  #right: HTMLElement | null = null;
  #spec: AppBarSpec = {};
  #items: Map<string, AppBarItem> = new Map();
  // Persistent, per-id order assigned on first sight and never cleared, so an
  // item retracted by an extension unload drops back into its original slot.
  #order: Map<string, number> = new Map();
  #nextOrder = 0;

  /**
   * Build the bar and apply the profile spec. Safe to call twice — the second
   * call re-renders the same element instead of stacking a second bar.
   */
  init(spec?: AppBarSpec): void {
    if (typeof document === 'undefined') return;
    this.#spec = spec ?? {};
    this.#injectStyle();

    const height = this.#spec.height ?? DEFAULT_HEIGHT;
    document.documentElement.style.setProperty(HEIGHT_VAR, `${height}px`);
    document.documentElement.dataset[ROOT_FLAG] = 'on';

    this.#mount();
    for (const item of this.#spec.items ?? []) this.addItem(item);
    void this.#renderTitle();
  }

  addItem(item: AppBarItem): void {
    if (!item?.id) return;
    this.#items.set(item.id, item);
    if (!this.#order.has(item.id)) this.#order.set(item.id, this.#nextOrder++);
    if (!this.#bar) return;
    this.#renderItem(item);
  }

  removeItem(itemId: string): void {
    this.#items.delete(itemId);
    this.#bar?.querySelector(`[data-appbar-item='${itemId}']`)?.remove();
  }

  #injectStyle(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = appBarCss;
    document.head.appendChild(style);
  }

  /**
   * A fixed `document.body` child rather than a layout slot: `applyProfile`
   * clears `#punica-editor` on every welcome↔classic switch, and the host calls
   * `Layout.render()` after module start, so a slot-based bar would need
   * re-mount bookkeeping to survive either.
   */
  #mount(): void {
    let bar = document.getElementById(BAR_ID);
    if (!bar) {
      bar = document.createElement('div');
      bar.id = BAR_ID;
      document.body.appendChild(bar);
    }
    bar.innerHTML = '';
    this.#bar = bar;

    this.#left = this.#region('appbar-region-left');
    this.#center = this.#region('appbar-region-center');
    this.#right = this.#region('appbar-region-right');
    bar.append(this.#left, this.#center, this.#right);
  }

  #region(className: string): HTMLElement {
    const region = document.createElement('div');
    region.className = `appbar-region ${className}`;
    return region;
  }

  /** Brand mark on the left, workspace (or product) name in the middle. */
  async #renderTitle(): Promise<void> {
    const product = this.#productName();
    const brand = this.#renderBrand(product);
    if (this.#left && brand) this.#left.prepend(brand);

    const mode = this.#spec.title ?? 'workspace';
    if (!this.#center || mode === 'none') return;
    const text =
      mode === 'product' ? product : ((await workspaceName()) ?? product);
    if (!text) return;
    const title = document.createElement('div');
    title.className = 'appbar-title';
    title.textContent = text;
    title.title = text;
    this.#center.appendChild(title);
  }

  /**
   * The profile's logo, and the product name only as its alt text and tooltip.
   *
   * The name used to be drawn beside the mark and was dropped: the strip is
   * already crowded on the left (the mark, and a menu button on hosts that hide
   * their menu bar), the centre names the workspace, and the OS window title and
   * taskbar entry both say the product — three places for a string that never
   * changes. The mark carries identity on its own.
   */
  #renderBrand(product: string | null): HTMLElement | null {
    const logo = this.#branding()?.logo;
    if (!logo) return null;

    const branding = document.createElement('div');
    branding.className = 'appbar-branding';
    if (product) branding.title = product;

    const mark = document.createElement('img');
    mark.className = 'appbar-logo';
    mark.src = logo;
    mark.alt = product ?? '';
    const slot = document.createElement('span');
    slot.className = 'appbar-mark';
    slot.appendChild(mark);
    branding.appendChild(slot);
    return branding;
  }

  #branding(): { productName?: string; logo?: string } | undefined {
    try {
      return punica.shell.Profile.manager.registry.getActive()?.identity
        ?.branding;
    } catch {
      return undefined;
    }
  }

  #productName(): string | null {
    try {
      const profile = punica.shell.Profile.manager.registry.getActive();
      return (
        profile?.identity?.branding?.productName ??
        profile?.identity?.name ??
        null
      );
    } catch {
      return null;
    }
  }

  #renderItem(item: AppBarItem): void {
    const container =
      (item.alignment ?? 'right') === 'left' ? this.#left : this.#right;
    if (!container) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'appbar-item';
    wrapper.dataset.appbarItem = item.id;

    const button = document.createElement('button');
    button.type = 'button';
    button.className =
      item.kind === 'avatar' ? 'appbar-avatar' : 'appbar-action';
    button.title = item.title ?? item.label ?? item.id;
    button.setAttribute('aria-label', button.title);

    if (item.kind === 'avatar') {
      button.textContent = (item.label ?? item.title ?? '?').trim().charAt(0);
      if (!button.textContent && item.icon) button.innerHTML = item.icon;
      // Identity, if the host can name it. Deliberately not awaited: the glyph
      // above is already correct enough to paint, and a slow host must not hold
      // the bar's first render.
      void this.#fillInitials(button, item);
    } else {
      const parts: string[] = [];
      if (item.icon) parts.push(item.icon);
      if (item.label) parts.push(`<span>${item.label}</span>`);
      button.innerHTML = parts.join('');
    }

    wrapper.appendChild(button);
    if (item.menu?.length) {
      this.#attachMenu(wrapper, button, item);
    } else if (item.command) {
      const commandId = item.command;
      button.addEventListener('click', () => execute(commandId));
    }

    this.#insertOrdered(container, wrapper, item.id);
  }

  /**
   * Dropdown for an item that declares `menu`. Entries whose command is not
   * registered are dropped, so a host that lacks the surface (a browser tab has
   * no licence activation) shows a shorter menu rather than dead rows.
   *
   * The filter runs on every open, not once at build time: the bar is built
   * during module start, while the commands it points at are registered later by
   * extension load (`settings.open`) or by the host (`host.*`). Deciding at
   * build time would empty every menu.
   */
  #attachMenu(
    wrapper: HTMLElement,
    button: HTMLElement,
    item: AppBarItem
  ): void {
    const menu = Helpers.createMenu({ className: 'appbar-menu' });
    const byId = new Map<string, AppBarMenuItem>();
    wrapper.appendChild(menu);

    const build = (summaryRows: HTMLElement[]): number => {
      const known = registeredCommands();
      byId.clear();
      menu.innerHTML = '';
      for (const row of summaryRows) menu.appendChild(row);
      for (const entry of item.menu ?? []) {
        if (!hasCommand(known, entry.command)) continue;
        byId.set(entry.id, entry);
        menu.appendChild(
          Helpers.createMenuItem({
            text: entry.label,
            attrs: { 'data-item': entry.id, 'aria-label': entry.label }
          })
        );
      }
      return byId.size;
    };

    button.addEventListener('pointerdown', (ev: Event) => {
      // Both stop-the-event calls have to happen synchronously; opening the
      // menu is what waits.
      ev.stopPropagation();
      ev.preventDefault();
      void this.#openMenu(menu, button, item, build);
    });
    menu.addEventListener('close', () => menu.removeAttribute('open'));
    menu.addEventListener('menu-item-click', (event: Event) => {
      const detail = (event as CustomEvent).detail as
        { data?: string } | undefined;
      const entry = detail?.data ? byId.get(detail.data) : undefined;
      event.stopPropagation();
      menu.removeAttribute('open');
      if (entry) execute(entry.command);
    });
  }

  /**
   * Avatar glyph from `initialsCommand`, at most two characters.
   *
   * Silent about every failure on purpose: a host with no account, an
   * unregistered command and a thrown one all leave the label's first letter in
   * place, which is what the bar showed before this existed.
   */
  async #fillInitials(button: HTMLElement, item: AppBarItem): Promise<void> {
    const commandId = item.initialsCommand;
    if (!commandId || !hasCommand(registeredCommands(), commandId)) return;
    try {
      const value =
        await punica.kernel.Commands.manager.executeCommand(commandId);
      const text = typeof value === 'string' ? value.trim().slice(0, 2) : '';
      if (text) button.textContent = text;
    } catch {
      /* the glyph keeps the label's first letter */
    }
  }

  /**
   * Build the menu, then open it.
   *
   * The order is the whole point, and it is not a style choice: `punica-menu`
   * clones its children when it opens and keeps ONLY `<punica-menu-item>`
   * nodes. So the header has to be menu items (a `<div>` is silently dropped)
   * and it has to be in the DOM BEFORE `openMenuAt` (anything appended after
   * the clone is never rendered). This is why the licence header never once
   * appeared in a real window while the unit tests were green: jsdom has no
   * `punica-menu`, so it filtered nothing and cloned nothing.
   */
  async #openMenu(
    menu: HTMLElement,
    button: HTMLElement,
    item: AppBarItem,
    build: (summaryRows: HTMLElement[]) => number
  ): Promise<void> {
    const commandId = item.summaryCommand;
    const rows =
      commandId && hasCommand(registeredCommands(), commandId)
        ? await this.#summaryRows(commandId)
        : [];
    if (!build(rows)) {
      if (item.command) execute(item.command);
      return;
    }
    // The glyph lives on the bar's own button, outside the cloned menu, so it
    // can be refreshed without holding the menu open. Re-read here because a
    // licence activated mid-session changes who the app belongs to, and opening
    // the account menu is exactly when a stale initial would be looked at.
    if (item.kind === 'avatar') void this.#fillInitials(button, item);
    Helpers.openMenuAt(menu, { anchor: button, placement: 'bottom' });
  }

  /**
   * The menu's header rows (e.g. the licence, or who the app is licensed to).
   *
   * One row per line of the command's answer, first one emphasised: a name over
   * an address reads as identity, while the same two joined by a separator reads
   * as a sentence. Styling is inline rather than a class because the rendered
   * menu is cloned into `document.body`, out of reach of this module's scoped
   * stylesheet — the same reason `MenuItemOptions.innerHTML` exists.
   *
   * Returns [] for anything that is not a non-empty string, which is how a host
   * with nothing to say leaves the menu exactly as it was.
   */
  async #summaryRows(commandId: string): Promise<HTMLElement[]> {
    let text: unknown;
    try {
      text = await punica.kernel.Commands.manager.executeCommand(commandId);
    } catch {
      return []; // the header is decoration; a failure just leaves it out
    }
    if (typeof text !== 'string' || !text.trim()) return [];
    return text
      .trim()
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line, index) => {
        const label = document.createElement('span');
        // textContent, never innerHTML: this string comes from a host command
        // and can carry a user's own name and address.
        label.textContent = line;
        label.setAttribute(
          'style',
          [
            'display:block',
            // Clipped, not wrapped: an email address must not decide how wide
            // the menu is.
            'max-width:220px',
            'overflow:hidden',
            'text-overflow:ellipsis',
            'white-space:nowrap',
            'font-size:12px',
            index === 0 ? 'font-weight:600' : 'opacity:0.7'
          ].join(';')
        );
        return Helpers.createMenuItem({
          // Not an action: `disabled` is what tells assistive tech this row is
          // a label. The component still emits a click, which the menu's own
          // handler ignores because the row carries no `data-item`.
          attrs: {
            disabled: '',
            'data-appbar-summary': index === 0 ? 'lead' : 'line'
          },
          children: [label]
        });
      });
  }

  /** Insert at the item's persistent order slot, replacing any existing node. */
  #insertOrdered(
    container: HTMLElement,
    element: HTMLElement,
    id: string
  ): void {
    container.querySelector(`[data-appbar-item='${id}']`)?.remove();
    const order = this.#order.get(id) ?? this.#nextOrder++;
    let ref: Element | null = null;
    for (const child of Array.from(container.children)) {
      const childId = (child as HTMLElement).dataset?.appbarItem;
      const childOrder = childId ? this.#order.get(childId) : undefined;
      if (childOrder !== undefined && childOrder > order) {
        ref = child;
        break;
      }
    }
    container.insertBefore(element, ref);
  }
}

type AppBarNamespace = {
  manager: IAppBarManager & { init(spec?: AppBarSpec): void };
};

const appBar: AppBarNamespace = {
  manager: new AppBarManager()
};

export default appBar;
